import { api } from '../api.ts';
import { askQuestion, prepareDocuments } from '../aiQuery.ts';
import { SessionSearchIndex } from '../search.ts';
import { clear, el, formatDate, hinweis, karte } from '../ui.ts';
import { alleSafes } from '../safes.ts';
import { safeKacheln, safeMarke } from './safes-ui.ts';
import {
  AKTION_TEXT,
  sorgeFuerMetadaten,
  suchbereichSetzen,
  fehlertext,
  kopfzeile,
  oeffnen,
  protokollAkteur,
  root,
  sichtbareDokumente,
  sorgeFuerEntschluesselte,
  state,
  type LogRow,
} from './gemeinsam.ts';

/**
 * Startseite (nur Kunden): erst die Handgriffe, dann die Frage an die
 * eigenen Unterlagen, zuletzt der Rückblick.
 *
 * Die Reihenfolge ist Absicht: Wer alle paar Wochen vorbeikommt, kommt mit
 * einer Aufgabe – die Knöpfe dafür stehen oben, nicht hinter einem Feed.
 *
 * Die Startseite entschlüsselt nichts von sich aus. Erst „Suchen" oder
 * „Frage beantworten" lädt die Unterlagen – so ist der Einstieg auch mit
 * vielen Dokumenten sofort da.
 */

export async function renderStart(): Promise<void> {
  clear(root);
  if (state.me?.role === 'customer') {
    // Metadaten (Safes, Liste, Ordner) sind ein schneller Abruf – die Kacheln „Wo suchen?"
    // und die Reiter brauchen sie. Ein Fehler hier zeigt sich später in der Suche.
    root.append(kopfzeile('start'), el('p', { class: 'loading' }, 'Wird geladen …'));
    await sorgeFuerMetadaten().catch(() => undefined);
    clear(root);
  }
  root.append(kopfzeile('start'), ...(state.me?.gast ? [gastKarte()] : []), aktionenKarte(), frageSucheKarte(), feedKarte());
}

// --- Was möchten Sie tun? ---------------------------------------------------

function aktionsKnopf(text: string, erklaerung: string, zielHash: string): HTMLElement {
  return el(
    'a',
    { class: 'aktion-knopf', href: zielHash },
    el('span', { class: 'aktion-text' }, el('strong', {}, text), el('span', { class: 'leise' }, erklaerung)),
    el('span', { class: 'aktion-pfeil', 'aria-hidden': 'true' }, '›'),
  );
}

function aktionenKarte(): HTMLElement {
  return karte(
    el('h2', {}, 'Was möchten Sie tun?'),
    el(
      'div',
      { class: 'aktion-liste' },
      aktionsKnopf(
        'Neues Dokument ablegen',
        'Foto oder PDF – wird auf Ihrem Gerät verschlüsselt',
        '#/dokumente',
      ),
      aktionsKnopf(
        'Dokumente freigeben',
        'Ihrem Ansprechpartner befristet Zugriff geben',
        '#/freigaben',
      ),
      aktionsKnopf('Meine Dokumente ansehen', 'Ordner und abgelegte Unterlagen', '#/dokumente'),
    ),
  );
}

/** Gastkonto: ohne eigenen Safe, nur fremde – dezent gesagt, mit dem Weg zur Buchung. */
function gastKarte(): HTMLElement {
  const geoeffnet = alleSafes().filter((s) => s.zugang === 'gast' || (s.zugang === 'mitglied' && s.typ !== 'privat'));
  return karte(
    el('h2', {}, 'Sie nutzen den Akten-Safe als Gast'),
    el(
      'p',
      {},
      geoeffnet.length
        ? `Für Sie geöffnet: ${geoeffnet.map((s) => s.titel).join(', ')}. Einen eigenen Safe für Ihre Unterlagen haben Sie noch nicht.`
        : 'Der Schlüssel zu dem Safe, zu dem Sie eingeladen wurden, kommt automatisch, sobald sich der Besitzer das nächste Mal anmeldet.',
    ),
    el(
      'p',
      { class: 'leise' },
      'Einen eigenen Safe – verschlüsselt, mit Suche und Fragen an Ihre Unterlagen – buchen Sie bei ',
      el('a', { href: 'https://it-karsten-fischer.de', target: '_blank', rel: 'noopener' }, 'IT-Service Karsten Fischer'),
      '. Ihr Konto und Passwort bleiben dabei bestehen.',
    ),
  );
}

// --- Fragen und Suchen – ein Feld, zwei Wege --------------------------------

export function frageSucheKarte(): HTMLElement {
  const ausgabe = el('div', { id: 'start-suche-ausgabe' });
  // Der Buddy arbeitet an fremden Unterlagen, und zwar an denen genau eines
  // Kunden. Deshalb steht dessen Name in der Überschrift: Wer zwischen
  // mehreren Freigaben hin und her wechselt, soll nie raten müssen, wessen
  // Unterlagen er gerade befragt.
  const fuerBuddy = state.me?.role === 'buddy';
  const kunde = fuerBuddy
    ? sichtbareDokumente()[0]?.meta.kunde_name ?? null
    : null;

  const eingabe = el('input', {
    type: 'search',
    id: 'start-suche',
    placeholder: fuerBuddy
      ? 'z. B. Mietvertrag – oder: Wann läuft die Kfz-Versicherung ab?'
      : 'z. B. Telekom-Rechnung – oder: Wie viel habe ich für den Laptop bezahlt?',
  }) as HTMLInputElement;

  eingabe.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') void suchen(eingabe, ausgabe);
  });

  // Familien-Safes: eine Frage, große Kacheln – nur, wenn es mehr als einen Safe gibt.
  const safes = fuerBuddy ? [] : alleSafes();
  const kachelPlatz = el('div', {});
  const zeichneKacheln = (): void => {
    clear(kachelPlatz);
    if (safes.length < 2) return;
    kachelPlatz.append(
      safeKacheln({
        frage: 'Wo möchten Sie suchen?',
        safes,
        gewaehlt: state.suchbereich,
        mitUeberall: true,
        name: 'suchbereich',
        zeile: (safe) => {
          const n = state.documents.filter((d) => (d.safe_kunde_id ?? safes[0]?.kundeId) === safe.kundeId).length;
          return safe.gesperrt ? null : `${n} Dokument${n === 1 ? '' : 'e'}`;
        },
        onWahl: (kundeId) => {
          suchbereichSetzen(kundeId);
          clear(ausgabe);
          zeichneKacheln();
        },
      }),
    );
  };
  zeichneKacheln();

  return karte(
    el(
      'h2',
      {},
      fuerBuddy
        ? kunde
          ? `Unterlagen von ${kunde} durchsuchen`
          : 'Freigegebene Unterlagen durchsuchen'
        : 'Fragen Sie Ihre Dokumente',
    ),
    kachelPlatz,
    el('label', { for: 'start-suche' }, 'Suchbegriff oder Frage'),
    eingabe,
    el(
      'p',
      { class: 'leise' },
      fuerBuddy
        ? '„Suchen" findet Begriffe und läuft vollständig auf diesem Gerät. ' +
          '„Frage beantworten" schickt die passenden Auszüge an die KI — dieser ' +
          'Zugriff steht anschließend im Protokoll des Kunden. Beides umfasst ' +
          'ausschließlich die oben gewählte Freigabe.'
        : '„Suchen" findet Begriffe und läuft vollständig auf diesem Gerät. ' +
          '„Frage beantworten" lässt unsere KI eine Antwort aus Ihren Unterlagen ' +
          'zusammenstellen und nennt die Fundstellen zum Nachprüfen.',
    ),
    ausgabe,
    el(
      'div',
      { class: 'knopfreihe' },
      el('button', { id: 'start-suchen', onclick: () => void suchen(eingabe, ausgabe) }, 'Suchen'),
      el(
        'button',
        { id: 'start-fragen', class: 'sekundaer', onclick: () => void fragen(eingabe, ausgabe) },
        'Frage beantworten',
      ),
    ),
  );
}

async function mitEntschluesselten(ausgabe: HTMLElement): Promise<boolean> {
  if (state.opened.length === 0 && state.documents.length === 0) {
    ausgabe.append(el('p', { class: 'loading' }, 'Unterlagen werden entschlüsselt …'));
  }
  try {
    await sorgeFuerEntschluesselte();
    return true;
  } catch (error) {
    clear(ausgabe);
    ausgabe.append(hinweis(fehlertext(error), 'warnung'));
    return false;
  }
}

async function suchen(eingabe: HTMLInputElement, ausgabe: HTMLElement): Promise<void> {
  const begriff = eingabe.value.trim();
  clear(ausgabe);
  if (begriff.length < 3) {
    ausgabe.append(hinweis('Bitte mindestens drei Zeichen eingeben.', 'warnung'));
    return;
  }

  if (!(await mitEntschluesselten(ausgabe))) return;
  clear(ausgabe);

  // Für den Buddy ist das die Freigabe genau eines Kunden, nicht alles, was
  // ihm gerade freigegeben ist (siehe `sichtbareDokumente`).
  const durchsuchbar = sichtbareDokumente();
  if (durchsuchbar.length === 0) {
    ausgabe.append(
      el(
        'p',
        { class: 'leise' },
        state.me?.role === 'buddy'
          ? 'Zu dieser Freigabe liegen keine Dokumente vor.'
          : state.suchbereich
            ? 'In diesem Safe liegen noch keine Dokumente.'
            : 'Sie haben noch keine Dokumente abgelegt.',
      ),
    );
    return;
  }

  if (!state.index) {
    ausgabe.append(el('p', { class: 'loading' }, 'Suchindex wird aufgebaut …'));
    state.index = new SessionSearchIndex();
    await state.index.build(durchsuchbar);
    clear(ausgabe);
  }

  const hits = state.index.search(begriff);
  if (hits.length === 0) {
    ausgabe.append(el('p', { class: 'leise' }, 'Keine Treffer.'));
    return;
  }

  ausgabe.append(
    el(
      'ul',
      { class: 'dokumentliste' },
      ...hits.map((hit) =>
        el(
          'li',
          {},
          el(
            'div',
            {},
            el('div', { class: 'dokumentname' }, hit.filename, safeMarke(state.opened.find((d) => d.id === hit.documentId)?.meta.safe_kunde_id)),
            el('div', { class: 'leise' }, hit.snippet),
          ),
          el('button', { class: 'sekundaer', onclick: () => void oeffnen(hit.documentId) }, 'Öffnen'),
        ),
      ),
    ),
  );
}

async function fragen(eingabe: HTMLInputElement, ausgabe: HTMLElement): Promise<void> {
  const frage = eingabe.value.trim();
  const knopf = root.querySelector<HTMLButtonElement>('#start-fragen');

  clear(ausgabe);
  if (!frage) {
    ausgabe.append(hinweis('Bitte geben Sie eine Frage ein.', 'warnung'));
    return;
  }

  if (knopf) knopf.disabled = true;
  try {
    if (!(await mitEntschluesselten(ausgabe))) return;

    const befragbar = sichtbareDokumente();
    if (befragbar.length === 0) {
      clear(ausgabe);
      ausgabe.append(
        el(
          'p',
          { class: 'leise' },
          state.me?.role === 'buddy'
            ? 'Zu dieser Freigabe liegen keine Dokumente vor.'
            : state.suchbereich
              ? 'In diesem Safe liegen noch keine Dokumente.'
              : 'Sie haben noch keine Dokumente abgelegt.',
        ),
      );
      return;
    }

    if (!state.prepared) {
      clear(ausgabe);
      ausgabe.append(el('p', { class: 'loading' }, 'Unterlagen werden vorbereitet …'));
      state.prepared = await prepareDocuments(befragbar);
    }
    clear(ausgabe);
    ausgabe.append(el('p', { class: 'loading' }, 'Antwort wird zusammengestellt …'));

    const result = await askQuestion(frage, state.prepared);

    clear(ausgabe);
    ausgabe.append(el('div', { class: 'antwort' }, result.answer));

    if (result.sources.length > 0) {
      ausgabe.append(
        el(
          'div',
          { class: 'quellen' },
          el('strong', {}, 'Grundlage dieser Antwort:'),
          el(
            'ul',
            {},
            // Jeder Beleg ist ein Knopf, kein bloßer Name: Wer nachprüfen
            // will, ob die Antwort stimmt, soll das Dokument von hier aus
            // öffnen können. `oeffnen` läuft direkt im Klick-Handler an,
            // damit das neue Tab noch als nutzergesteuert gilt.
            ...result.sources.map((source) =>
              el(
                'li',
                {},
                el(
                  'button',
                  {
                    type: 'button',
                    class: 'quellen-chip',
                    'aria-label': `„${source.filename}" öffnen`,
                    onclick: () => void oeffnen(source.documentId),
                  },
                  source.filename,
                  safeMarke(state.opened.find((d) => d.id === source.documentId)?.meta.safe_kunde_id),
                  el('span', { class: 'chip-zeichen', 'aria-hidden': 'true' }, '↗'),
                ),
              ),
            ),
          ),
          el(
            'p',
            { class: 'leise' },
            'Tippen Sie auf ein Dokument, um es zu öffnen und die Antwort nachzuprüfen.',
          ),
        ),
      );
    }
  } catch (error) {
    clear(ausgabe);
    ausgabe.append(hinweis(fehlertext(error), 'warnung'));
  } finally {
    if (knopf) knopf.disabled = false;
  }
}

// --- Was passierte zuletzt --------------------------------------------------

const FEED_ANZAHL = 3;

function feedKarte(): HTMLElement {
  const container = el('div', { id: 'start-feed' }, el('p', { class: 'loading' }, 'Wird geladen …'));
  void ladeFeed(container);

  return karte(
    el('h2', {}, 'Was passierte zuletzt'),
    el(
      'p',
      { class: 'leise' },
      'Jeder Zugriff auf Ihre Unterlagen wird festgehalten – auch unsere. ' +
        'Was in den Dokumenten steht, sieht außer Ihnen niemand.',
    ),
    container,
  );
}

async function ladeFeed(container: HTMLElement): Promise<void> {
  let entries: LogRow[];
  try {
    ({ entries } = await api.get<{ entries: LogRow[] }>(`/api/access-log?limit=${FEED_ANZAHL}`));
  } catch (error) {
    clear(container);
    container.append(hinweis(fehlertext(error), 'warnung'));
    return;
  }

  clear(container);

  if (entries.length === 0) {
    container.append(
      el(
        'p',
        { class: 'leise' },
        'Hier ist noch nichts passiert. Legen Sie Ihr erstes Dokument ab – ' +
          'ein Foto genügt, den Rest übernimmt die Anwendung.',
      ),
      el(
        'div',
        { class: 'knopfreihe' },
        el('a', { class: 'aktion-knopf schmal', href: '#/dokumente' },
          el('span', { class: 'aktion-text' }, el('strong', {}, 'Erstes Dokument ablegen')),
          el('span', { class: 'aktion-pfeil', 'aria-hidden': 'true' }, '›'),
        ),
      ),
    );
    return;
  }

  container.append(
    el(
      'ul',
      { class: 'feed-liste' },
      ...entries.map((entry) =>
        el(
          'li',
          {},
          el('strong', {}, AKTION_TEXT[entry.action] ?? entry.action),
          el(
            'span',
            { class: 'leise' },
            `${formatDate(entry.accessed_at)} · ${protokollAkteur(entry)}` +
              (entry.detail?.documentCount ? ` · ${entry.detail.documentCount} Dokumente` : ''),
          ),
        ),
      ),
    ),
    el(
      'div',
      { class: 'knopfreihe' },
      el('a', { class: 'sekundaer knopf-link', href: '#/protokoll' }, 'Vollständiges Protokoll'),
    ),
  );
}
